import { addDays, format, parse } from 'date-fns'
import { useSelector } from 'react-redux'

import { selectLastRecurrenceSuccess } from '@/features/domain/history'
import { selectStorageSpaceData } from '@/features/domain/order'
import { selectUserConfiguration, selectUserType } from '@/features/domain/user'
import { LinearProgress, Stack, Typography } from '@mui/material'

import { useCheckEligibleRestoreRecurrentOrders } from '../hooks/useCheckEligibleRestoreRecurrentOrders'
import { usePlanningRange } from '../hooks/usePlanningRange'
import { useRestoreOrders } from '../hooks/useRestoreOrders'
import { useTexts } from '../hooks/useTexts'
import { getAlertConfig } from '../utils/getAlertConfig'
import { RecurrentOrdersQueueAlert } from './RecurrentOrdersQueueAlert'
import { RestoreOrdersCard } from './RestoreOrdersCard'
import { StorageAlert } from './StorageAlert'
import { SuggestedActions } from './SuggestedActions'

type Props = {
  navigateToOrders: () => void
  openEditPlanningRange: () => void
}

export function StorageSection(props: Props) {
  const { navigateToOrders, openEditPlanningRange } = props

  const { percentage, currentOrders, totalOrders } = useSelector(selectStorageSpaceData)
  const lastRecurrenceSuccess = useSelector(selectLastRecurrenceSuccess)
  const { today, planType } = useSelector(selectUserConfiguration)
  const userType = useSelector(selectUserType)
  const { restoreOrders } = useRestoreOrders()
  const { planningRange } = usePlanningRange()
  const { loading, restorable } = useCheckEligibleRestoreRecurrentOrders()
  const texts = useTexts()

  const alertConfig = getAlertConfig(percentage, lastRecurrenceSuccess, today, texts, planType)
  const percentageString = `${percentage}%`

  const isDisplaySuggestedActions = alertConfig?.severity === 'error' && userType !== 'viewer'

  const formattedToday = parse(today, 'yyyyMMdd', new Date())
  const todayPlusPlanningRange = addDays(formattedToday, planningRange)
  const formattedTodayPlusPlanningRange = format(todayPlusPlanningRange, 'yyyyMMdd')

  const showRestoreOrders =
    (userType === 'admin' || userType === 'planner') &&
    planType === 'operations' &&
    restorable &&
    !loading // Prevent flash during initial load - only show when we have definitive restore data

  const showRecurrentOrdersQueueAlert =
    lastRecurrenceSuccess &&
    lastRecurrenceSuccess < formattedTodayPlusPlanningRange &&
    !loading &&
    !restorable

  return (
    <Stack spacing={4} mb={4} data-testid="storage-section">
      <Stack spacing={2} data-testid="storage-details">
        <Typography variant="subtitle2" data-testid="storage-label">
          {texts.storageTitle}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2} width="100%">
          <LinearProgress
            data-testid="percentage-progress-bar"
            variant="determinate"
            value={percentage}
            color={alertConfig?.severity || 'primary'}
            sx={{
              height: 8,
              flex: 1,
              borderRadius: '2px',
            }}
          />
          <Typography variant="body2" data-testid="percentage">
            {percentageString}
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary" data-testid="order-count-status">
          {texts.storageLinearProgressLabel(currentOrders, totalOrders)}
        </Typography>

        <StorageAlert config={alertConfig} lastRecurrenceSuccess={lastRecurrenceSuccess} />

        {showRecurrentOrdersQueueAlert && (
          <RecurrentOrdersQueueAlert lastRecurrenceSuccess={lastRecurrenceSuccess} />
        )}
      </Stack>

      {showRestoreOrders && <RestoreOrdersCard onRestoreOrders={restoreOrders} loading={loading} />}

      {isDisplaySuggestedActions && (
        <SuggestedActions
          navigateToOrders={navigateToOrders}
          openEditPlanningRange={openEditPlanningRange}
          planType={planType}
        />
      )}
    </Stack>
  )
}
